// ABOUTME: Página del modo foto: selfie → estilo → función de Firebase → foto con rastas,
// ABOUTME: compartir y comprar.
import { createSegmenter, type Segmenter } from "../calibration/segmenter";
import { track, trackOnce } from "./analytics";
import { buyUrl, newShotId, shareText, tryOnUrl } from "./attribution";
import { generateTryOn, generateViews, logGeneration, pollOrbit, rateGeneration, startOrbit, TryOnError } from "./api";
import { summarizeChecks } from "./quality";
import { pickOrbitFrames, scrubTime, type YawSample } from "./orbit";
import { faceYaw, mirrorBlob } from "./facing";
import { isFeatureOn } from "./features";
import { composeHairOnly, watermarkOnly } from "./composite";
import { prepareSelfie } from "./image";
import { HAIRSTYLES, PHOTO_STYLES } from "./styles";
import "./style.css";

const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL ?? "";
/** Botón «Girar 360°» (vídeo Veo y, de respaldo, las 3 vistas). Apagado en la primera versión: VITE_ORBIT_ENABLED=on (o 1/true/yes/si, ver features.ts) lo reactiva. */
const ORBIT_ENABLED = isFeatureOn(import.meta.env.VITE_ORBIT_ENABLED);

function el<T extends HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

function setStatus(text: string, isError = false): void {
  const s = el<HTMLParagraphElement>("status");
  s.textContent = text;
  s.hidden = text === "";
  s.classList.toggle("error", isError);
}

let selfieBase64: string | null = null;
let selfieDataUrl: string | null = null;
let selfieAspect = "3:4";
let selfiePadded = false;
let selfieFile: File | null = null;
let mirror = true;
let beautify = 0;
const BEAUTY_HINTS = [
  "Sin retoque: tu selfie tal cual, solo cambia el pelo.",
  "Suave: corrige imperfecciones de la piel, nada más. El resto, igual que tu selfie.",
  "Glam: retoque editorial con piel perfecta y luz de estudio.",
  "Portada: la versión de revista, con todo. Seguís siendo vos.",
];
// Segmentador para la composición por máscara; se carga en segundo plano.
let segmenter: Segmenter | null = null;
const segmenterReady: Promise<Segmenter | null> = createSegmenter().then(
  (s) => {
    segmenter = s;
    return s;
  },
  (err: Error) => {
    console.warn("Sin segmentador: se mostrará la imagen generada tal cual", err.message);
    return null;
  },
);
let styleId: string | null = PHOTO_STYLES[0].id;
let kind: "full" | "crown" | "natural" | "wavy" | "waves" = "full";
let hairstyleId = HAIRSTYLES[0].id;
interface View {
  blob: Blob;
  /** Sin marca de agua: es lo que se envía para pedir otras vistas. */
  clean: Blob;
  url: string;
  caption: string;
}

interface Shot {
  name: string;
  productUrl: string;
  /** Id del registro de calidad en Firestore, cuando exista. */
  generationId?: string;
  /** Identificador corto de la foto: va en los UTM de compra y en Firestore, para cruzar ventas con fotos. */
  shotId: string;
  /** Texto de la marca de agua tal como salió en la foto (no depende del color seleccionado después). */
  label: string;
  styleId: string;
  hairstyleId: string;
  /** Selfie preparada (sin marca) con la que se hizo la foto: es la entrada del vídeo 360. */
  selfieBase64: string;
  views: View[];
  currentView: number;
  card: HTMLElement;
  /** Vídeo 360 ya generado (URL de blob). */
  orbitUrl?: string;
}
const shots: Shot[] = [];
let currentShot = 0;

function renderDots(container: HTMLElement, count: number, active: number): void {
  container.innerHTML = "";
  for (let i = 0; i < count; i++) {
    const d = document.createElement("span");
    if (i === active) d.classList.add("active");
    container.append(d);
  }
}

function currentViewOf(shot: Shot): View {
  return shot.views[Math.max(0, Math.min(shot.views.length - 1, shot.currentView))];
}

function setCurrentShot(i: number): void {
  currentShot = Math.max(0, Math.min(shots.length - 1, i));
  const shot = shots[currentShot];
  if (!shot) return;
  el<HTMLAnchorElement>("buy").href = buyUrl(shot.productUrl, "app", shot);
  renderDots(el<HTMLDivElement>("dots"), shots.length, currentShot);
}

const ANGLE_LABELS = ["Frente", "Lado", "Lado", "Espaldas"];

function showView(shot: Shot, index: number): void {
  shot.currentView = Math.max(0, Math.min(shot.views.length - 1, index));
  const view = currentViewOf(shot);
  const main = shot.card.querySelector<HTMLImageElement>(".main")!;
  main.src = view.url;
  main.alt = view.caption;
  main.hidden = false;
  const video = shot.card.querySelector<HTMLVideoElement>(".orbit")!;
  video.hidden = true;
  video.pause();
  shot.card.querySelector<HTMLElement>(".orbit-overlay")!.hidden = true;
  shot.card.querySelector<HTMLElement>(".caption")!.textContent = view.caption;
  shot.card.querySelectorAll<HTMLButtonElement>(".angle").forEach((b) => b.classList.toggle("active", b.dataset.index === String(shot.currentView)));
  shot.card.querySelector<HTMLButtonElement>(".orbit-btn")?.classList.remove("active");
}

/** Muestra el vídeo 360 en la tarjeta, con arrastre para girar. */
function showOrbit(shot: Shot): void {
  const video = shot.card.querySelector<HTMLVideoElement>(".orbit")!;
  const main = shot.card.querySelector<HTMLImageElement>(".main")!;
  main.hidden = true;
  video.hidden = false;
  shot.card.querySelector<HTMLElement>(".orbit-overlay")!.hidden = false;
  shot.card.querySelector<HTMLElement>(".caption")!.textContent = `${shot.name} · 360°`;
  shot.card.querySelectorAll<HTMLButtonElement>(".angle").forEach((b) => b.classList.remove("active"));
  shot.card.querySelector<HTMLButtonElement>(".orbit-btn")?.classList.add("active");
  shot.currentView = 0;
  // Una vuelta automática al abrir; después manda el dedo. Se salta el arranque, donde el
  // vídeo aún muestra la selfie sin rastas.
  video.currentTime = ORBIT_START;
  void video.play();
  video.onended = () => {
    video.pause();
  };
}

function attachScrub(video: HTMLVideoElement): void {
  let dragging = false;
  let startX = 0;
  let startTime = 0;
  const width = () => video.clientWidth || 1;
  video.addEventListener("pointerdown", (e) => {
    dragging = true;
    startX = e.clientX;
    startTime = video.currentTime;
    video.pause();
    video.setPointerCapture(e.pointerId);
  });
  video.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    const d = video.duration || 8;
    video.currentTime = ORBIT_START + scrubTime(startTime - ORBIT_START, e.clientX - startX, width(), d - ORBIT_START);
  });
  const stop = () => {
    dragging = false;
  };
  video.addEventListener("pointerup", stop);
  video.addEventListener("pointercancel", stop);
}

function appendView(shot: Shot, view: View): void {
  shot.views.push(view);
  const index = shot.views.length - 1;
  const angles = shot.card.querySelector<HTMLElement>(".angles")!;
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "angle";
  btn.dataset.index = String(index);
  btn.innerHTML = `<img alt="" /><span></span>`;
  btn.querySelector("img")!.src = view.url;
  btn.querySelector("span")!.textContent = ANGLE_LABELS[index] ?? "";
  btn.addEventListener("click", () => showView(shot, index));
  angles.append(btn);
  angles.hidden = shot.views.length < 2;
  showView(shot, index);
}

/** Botón a todo el ancho, encima de las miniaturas, para volver al vídeo 360. */
function appendOrbitButton(shot: Shot): void {
  const angles = shot.card.querySelector<HTMLElement>(".angles")!;
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "orbit-btn";
  btn.dataset.index = "orbit";
  btn.textContent = "Ver 360°";
  btn.addEventListener("click", () => showOrbit(shot));
  angles.before(btn);
  angles.hidden = false;
}

/**
 * Espera a que el fotograma del instante pedido esté pintado. En iOS Safari "seeked" llega
 * antes de que haya imagen decodificada; requestVideoFrameCallback sí garantiza el fotograma.
 */
async function seekTo(video: HTMLVideoElement, time: number): Promise<void> {
  const target = Math.min(Math.max(0, time), Math.max(0, (video.duration || 8) - 0.05));
  const rvfc = (video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number }).requestVideoFrameCallback;
  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => resolve(), 1500);
    const done = () => {
      window.clearTimeout(timeout);
      resolve();
    };
    video.addEventListener("error", () => reject(new Error("vídeo no legible")), { once: true });
    if (rvfc) {
      video.addEventListener("seeked", () => rvfc.call(video, done), { once: true });
    } else {
      video.addEventListener("seeked", () => window.setTimeout(done, 120), { once: true });
    }
    video.currentTime = target;
  });
}

function drawFrame(video: HTMLVideoElement, aspect: number): HTMLCanvasElement {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  let cw = vw;
  let ch = Math.round(vw / aspect);
  if (ch > vh) {
    ch = vh;
    cw = Math.round(vh * aspect);
  }
  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  canvas.getContext("2d")!.drawImage(video, Math.round((vw - cw) / 2), Math.round((vh - ch) / 2), cw, ch, 0, 0, cw, ch);
  return canvas;
}

function isBlack(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext("2d")!;
  const d = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  let sum = 0;
  for (let i = 0; i < d.length; i += 64) sum += d[i] + d[i + 1] + d[i + 2];
  return sum / (d.length / 64) < 12;
}

/** Fotograma del vídeo en un instante, recortado al centro con la proporción `aspect`, como JPEG. */
async function frameAt(video: HTMLVideoElement, time: number, aspect: number): Promise<Blob> {
  await seekTo(video, time);
  let canvas = drawFrame(video, aspect);
  if (isBlack(canvas)) {
    // Segundo intento: reproducir un instante para forzar la decodificación.
    await video.play().catch(() => undefined);
    await new Promise((r) => setTimeout(r, 150));
    video.pause();
    await seekTo(video, time);
    canvas = drawFrame(video, aspect);
  }
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob falló"))), "image/jpeg", 0.9));
}

const ORBIT_CAPTIONS = ["de lado", "de espaldas", "de lado"];
/** Segundos iniciales del vídeo en que Veo aún está "poniendo" las rastas. */
const ORBIT_START = 0.6;

async function onOrbit(shot: Shot, btn: HTMLButtonElement): Promise<void> {
  if (!FUNCTIONS_URL) return;
  const orbitStarted = performance.now();
  track("orbit_start", { style: shot.styleId, hairstyle: shot.hairstyleId });
  btn.disabled = true;
  btn.classList.add("busy");
  const label = btn.querySelector(".label")!;
  label.textContent = "Girando la foto… tarda medio minuto";
  setStatus("");
  try {
    // El vídeo parte de la selfie, no de la foto generada: Veo pone las rastas y gira.
    const { operationName, remainingToday, durationSeconds } = await startOrbit(FUNCTIONS_URL, shot.selfieBase64, selfieAspect, shot.styleId, shot.hairstyleId);
    const started = performance.now();
    const ticker = window.setInterval(() => {
      const s = Math.round((performance.now() - started) / 1000);
      label.textContent = s < 45 ? `Girando la foto… ${s} s` : "Ya casi está…";
    }, 1000);
    let videoBase64: string | undefined;
    let mime = "video/mp4";
    while (!videoBase64) {
      await new Promise((r) => setTimeout(r, 5000));
      const r = await pollOrbit(FUNCTIONS_URL, operationName).catch((e: Error) => {
        window.clearInterval(ticker);
        throw e;
      });
      if (r.done) {
        videoBase64 = r.videoBase64;
        mime = r.mimeType ?? mime;
      }
      if (performance.now() - started > 180_000) {
        window.clearInterval(ticker);
        throw new TryOnError("El vídeo tardó demasiado. Probá otra vez.", "DEADLINE_EXCEEDED");
      }
    }
    window.clearInterval(ticker);
    const bytes = Uint8Array.from(atob(videoBase64), (c) => c.charCodeAt(0));
    const videoBlob = new Blob([bytes], { type: mime });
    const video = shot.card.querySelector<HTMLVideoElement>(".orbit")!;
    shot.orbitUrl = URL.createObjectURL(videoBlob);
    video.src = shot.orbitUrl;
    // Veo entrega 9:16 con bandas negras alrededor de la foto 3:4: el escenario conserva la
    // proporción de la foto y el vídeo se recorta (object-fit: cover) a la zona central.
    const main = shot.card.querySelector<HTMLImageElement>(".main")!;
    shot.card.querySelector<HTMLElement>(".stage")!.style.aspectRatio = `${main.naturalWidth} / ${main.naturalHeight}`;
    await new Promise<void>((resolve, reject) => {
      video.addEventListener("canplay", () => resolve(), { once: true });
      video.addEventListener("error", () => reject(new Error("vídeo no legible")), { once: true });
      video.load();
    });
    attachScrub(video);
    // Ángulos: se muestrea el vídeo y se mide hacia dónde mira la cara en cada fotograma.
    label.textContent = "Sacando los ángulos…";
    const photoAspect = main.naturalWidth / main.naturalHeight;
    const duration = video.duration || durationSeconds;
    const samples: YawSample[] = [];
    for (let t = ORBIT_START + 0.15; t < duration - 0.2; t += 0.5) {
      const frame = await frameAt(video, t, photoAspect);
      samples.push({ time: t, yaw: await faceYaw(frame).catch(() => null) });
    }
    const pick = pickOrbitFrames(samples, duration);
    const times = [pick.right, pick.back, pick.left];
    for (let i = 0; i < times.length; i++) {
      const raw = await frameAt(video, times[i], photoAspect);
      const marked = await watermarkOnly(raw, shot.label, qrUrlFor(shot));
      appendView(shot, { blob: marked, clean: raw, url: URL.createObjectURL(marked), caption: `${shot.name} · ${ORBIT_CAPTIONS[i]}` });
    }
    btn.remove();
    appendOrbitButton(shot);
    showOrbit(shot);
    track("orbit_result", { ok: true, style: shot.styleId, ms: Math.round(performance.now() - orbitStarted) });
    setStatus(remainingToday > 0 ? `Te quedan ${remainingToday} fotos hoy` : "Esta era tu última foto de hoy");
  } catch (err) {
    console.warn("Vídeo 360 fallido, se usan las vistas fijas:", (err as Error).message);
    track("orbit_result", { ok: false, style: shot.styleId, code: err instanceof TryOnError ? err.code : "EXCEPTION" });
    label.textContent = "Ver de lado y de espaldas";
    btn.classList.remove("busy");
    btn.disabled = false;
    if (err instanceof TryOnError && err.code === "RESOURCE_EXHAUSTED") {
      setStatus(err.message, true);
      return;
    }
    setStatus("El vídeo 360 no está disponible ahora mismo. Te mostramos las vistas de lado y de espaldas.", true);
    await onViews(shot, btn);
  }
}

/** QR que va impreso en la foto en pantalla: ficha del producto etiquetada con canal «qr» y la foto. */
function qrUrlFor(shot: Pick<Shot, "productUrl" | "styleId" | "hairstyleId" | "shotId">): string {
  return buyUrl(shot.productUrl, "qr", shot);
}

function addShot(name: string, productUrl: string, frontal: View, origin: { styleId: string; hairstyleId: string; selfieBase64: string; shotId: string; label: string }): Shot {
  const card = document.createElement("figure");
  card.className = "polaroid";
  card.innerHTML = `
    <div class="stage">
      <img class="main" alt="" />
      <video class="orbit" playsinline muted preload="auto" hidden></video>
      <div class="orbit-overlay" hidden><img src="/brand/logo-white.png" alt="Dreadlove" /><span class="orbit-hint">Arrastrá para girar</span></div>
    </div>
    <div class="angles" hidden></div>
    <p class="caption"></p>
    <div class="rate">
      <span>¿Qué te pareció?</span>
      <button type="button" class="rate-btn" data-rating="1" aria-label="Me gusta">👍</button>
      <button type="button" class="rate-btn" data-rating="-1" aria-label="No me gusta">👎</button>
    </div>
    <div class="reasons" hidden>
      <span>¿Qué falló?</span>
      <button type="button" class="chipbtn" data-reason="color">El color</button>
      <button type="button" class="chipbtn" data-reason="cara">Mi cara</button>
      <button type="button" class="chipbtn" data-reason="pelo">El pelo</button>
      <button type="button" class="chipbtn" data-reason="otro">Otra cosa</button>
    </div>
    ${ORBIT_ENABLED ? '<button type="button" class="primary views-btn"><span class="spinner" aria-hidden="true"></span><span class="label">Girar 360°</span></button>' : ""}`;
  const shot: Shot = { name, productUrl, ...origin, views: [], currentView: 0, card };
  shots.push(shot);
  appendView(shot, frontal);
  card.querySelector<HTMLButtonElement>(".views-btn")?.addEventListener("click", (e) => void onOrbit(shot, e.currentTarget as HTMLButtonElement));
  attachRating(shot);
  el<HTMLDivElement>("gallery").append(card);
  el<HTMLElement>("result").hidden = false;
  setCurrentShot(shots.length - 1);
  requestAnimationFrame(() => card.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" }));
  return shot;
}

el<HTMLDivElement>("gallery").addEventListener("scroll", () => {
  const g = el<HTMLDivElement>("gallery");
  const i = Math.round(g.scrollLeft / (g.clientWidth + 14));
  if (i !== currentShot) setCurrentShot(i);
});

/** Progreso visible mientras se genera: fases con tiempos típicos; nunca llega al 100% sola. */
const PHASES: Array<[number, string]> = [
  [0, "Subiendo tu selfie…"],
  [2, "Poniendo las rastas… suele tardar unos 15 segundos"],
  [9, "Cuidando los detalles…"],
  [16, "Ya casi está…"],
];
let progressTimer: number | null = null;

function startProgress(): void {
  const section = el<HTMLElement>("progress");
  const bar = el<HTMLDivElement>("progress-bar");
  const text = el<HTMLParagraphElement>("progress-text");
  section.hidden = false;
  section.scrollIntoView({ behavior: "smooth", block: "center" });
  const started = performance.now();
  const tick = (): void => {
    const s = (performance.now() - started) / 1000;
    // Curva que se acerca al 90% a los ~20 s y se queda ahí hasta que llegue la foto.
    bar.style.width = `${Math.min(90, 90 * (1 - Math.exp(-s / 8)))}%`;
    const phase = [...PHASES].reverse().find(([at]) => s >= at);
    if (phase) text.textContent = phase[1];
  };
  tick();
  progressTimer = window.setInterval(tick, 500);
}

function finishProgress(): void {
  if (progressTimer !== null) window.clearInterval(progressTimer);
  progressTimer = null;
  el<HTMLDivElement>("progress-bar").style.width = "100%";
  window.setTimeout(() => {
    el<HTMLElement>("progress").hidden = true;
  }, 250);
}

/**
 * Un solo botón flotante con dos modos: sin selfie dice "Subir mi foto" y abre la cámara o la
 * galería; con selfie pasa a "Ver mi foto" y genera. Así nunca hay un botón que "no hace nada".
 */
function hasSelfie(): boolean {
  return Boolean(selfieBase64 && styleId);
}

function updateButton(): void {
  const btn = el<HTMLButtonElement>("generate");
  btn.textContent = hasSelfie() ? "Ver mi foto" : "Subir mi foto";
  btn.disabled = false;
}

/** Texto de la marca de agua: color o modelo y la web. */
function watermarkLabel(): string {
  const color = PHOTO_STYLES.find((s) => s.id === styleId)?.name ?? "";
  return `${color} · dreadlovearg.com`;
}

function styleName(): string {
  const color = PHOTO_STYLES.find((s) => s.id === styleId)?.name ?? "";
  const hair = HAIRSTYLES.find((h) => h.id === hairstyleId);
  const base = hair && hair.id !== HAIRSTYLES[0].id ? `${color} · ${hair.name.toLowerCase()}` : color;
  return beautify > 0 ? `${base} ${"✦".repeat(beautify)}` : base;
}

function renderHairstyles(): void {
  const grid = el<HTMLDivElement>("hairstyles");
  for (const hair of HAIRSTYLES) {
    const card = document.createElement("button");
    card.type = "button";
    card.className = "style" + (hair.id === hairstyleId ? " active" : "");
    card.innerHTML = `<img src="${hair.thumb}" alt="" loading="lazy" /><span>${hair.name}</span>`;
    card.addEventListener("click", () => {
      hairstyleId = hair.id;
      grid.querySelectorAll(".style").forEach((c) => c.classList.remove("active"));
      card.classList.add("active");
      track("select_hairstyle", { hairstyle: hair.id });
    });
    grid.append(card);
  }
}

function renderStyles(): void {
  const grid = el<HTMLDivElement>("styles");
  for (const style of PHOTO_STYLES) {
    if (style.kind === "crown") continue; // sin pestaña "Coronas": no se muestran ni se cargan sus miniaturas.
    const card = document.createElement("button");
    card.type = "button";
    card.className = "style" + (style.id === styleId ? " active" : "");
    card.dataset.kind = style.kind;
    card.hidden = style.kind !== kind;
    card.innerHTML = `<img src="${style.thumb}" alt="" loading="lazy" /><span>${style.name}</span>`;
    card.addEventListener("click", () => {
      styleId = style.id;
      grid.querySelectorAll(".style").forEach((c) => c.classList.remove("active"));
      card.classList.add("active");
      updateButton();
      track("select_style", { style: style.id, kind: style.kind });
    });
    grid.append(card);
  }
  document.querySelectorAll<HTMLButtonElement>(".tab").forEach((tab) => {
    tab.addEventListener("click", () => {
      kind = tab.dataset.kind as typeof kind;
      document.querySelectorAll(".tab").forEach((t) => t.classList.remove("active"));
      tab.classList.add("active");
      grid.querySelectorAll<HTMLButtonElement>(".style").forEach((c) => {
        c.hidden = c.dataset.kind !== kind;
      });
      const first = grid.querySelector<HTMLButtonElement>(`.style[data-kind="${kind}"]`);
      if (first && !PHOTO_STYLES.some((s) => s.id === styleId && s.kind === kind)) first.click();
    });
  });
}

async function onSelfie(file: File): Promise<void> {
  selfieFile = file;
  try {
    setStatus("Analizando el encuadre…");
    const prepared = await prepareSelfie(file, { mirror, segmenter: segmenter ?? (await segmenterReady) });
    setStatus(
      prepared.padded
        ? "Tu cara está muy cerca del borde: la foto saldrá con fondo de estudio y la cara recreada. Para el mejor resultado, sacá la selfie con aire por encima de la cabeza."
        : "",
    );
    selfieBase64 = prepared.base64;
    selfieDataUrl = prepared.dataUrl;
    selfieAspect = prepared.aspect;
    selfiePadded = prepared.padded;
    track("selfie_loaded", { padded: prepared.padded, aspect: prepared.aspect, mirror });
    const { dataUrl } = prepared;
    const preview = el<HTMLImageElement>("selfie-preview");
    preview.src = dataUrl;
    preview.hidden = false;
    document.getElementById("selfie-placeholder")!.setAttribute("hidden", "");
    el<HTMLSpanElement>("selfie-hint").lastChild!.textContent = "Cambiar la selfie";
    const mirrorBtn = el<HTMLButtonElement>("mirror");
    mirrorBtn.hidden = false;
    mirrorBtn.setAttribute("aria-pressed", String(mirror));
    updateButton();
  } catch (err) {
    setStatus(`No pude leer la foto: ${(err as Error).message}`, true);
  }
}

/** Generación en curso, para registrar el abandono si la persona cierra la página mientras espera. */
let generating: { styleId: string; startedAt: number } | null = null;

window.addEventListener("pagehide", () => {
  if (!generating) return;
  track("generate_abandon", { style: generating.styleId, ms: Math.round(performance.now() - generating.startedAt) });
});

async function onGenerate(): Promise<void> {
  if (!selfieBase64 || !styleId) return;
  if (!FUNCTIONS_URL) {
    setStatus("Falta configurar VITE_FUNCTIONS_URL", true);
    return;
  }
  const btn = el<HTMLButtonElement>("generate");
  btn.disabled = true;
  setStatus("");
  startProgress();
  const startedAt = performance.now();
  const shotId = newShotId();
  generating = { styleId, startedAt };
  track("generate", { style: styleId, hairstyle: hairstyleId, beautify, padded: selfiePadded, shot: shotId });
  try {
    const result = await generateTryOn(FUNCTIONS_URL, styleId, hairstyleId, selfieBase64, beautify, selfieAspect, selfiePadded);
    const qrUrl = qrUrlFor({ productUrl: result.productUrl, styleId, hairstyleId, shotId });
    const label = watermarkLabel();
    const bytes = Uint8Array.from(atob(result.imageBase64), (c) => c.charCodeAt(0));
    const generated = new Blob([bytes], { type: result.mimeType });
    let finalBlob = generated;
    let cleanBlob = generated;
    let composedOk = false;
    try {
      el<HTMLParagraphElement>("progress-text").textContent = "Ajustando los detalles…";
      // Con "embellecer" el retoque está en toda la foto, y con aire añadido el modelo recoloca
      // a la persona: en ambos casos la foto final es la generada entera, sin recomponer la cara.
      const canCompose = segmenter && selfieDataUrl && beautify === 0 && !selfiePadded;
      composedOk = Boolean(canCompose);
      if (canCompose) {
        const composed = await composeHairOnly(selfieDataUrl!, generated, segmenter!, { watermark: true, label, qrUrl });
        finalBlob = composed.blob;
        cleanBlob = composed.clean;
      } else {
        finalBlob = await watermarkOnly(generated, label, qrUrl);
      }
    } catch (err) {
      console.warn("Composición fallida, se usa la generada:", (err as Error).message);
    }
    generating = null;
    finishProgress();
    const ms = Math.round(performance.now() - startedAt);
    const composed = composedOk;
    track("generate_result", { ok: true, style: styleId, ms, composed, padded: selfiePadded, beautify, shot: shotId });
    const shot = addShot(styleName(), result.productUrl, { blob: finalBlob, clean: cleanBlob, url: URL.createObjectURL(finalBlob), caption: styleName() }, { styleId: styleId!, hairstyleId, selfieBase64: selfieBase64!, shotId, label });
    updateButton();
    setStatus(result.remainingToday > 0 ? `Te quedan ${result.remainingToday} fotos hoy` : "Esta era tu última foto de hoy");
    void logQuality(shot, cleanBlob, ms, composed);
  } catch (err) {
    generating = null;
    finishProgress();
    const message = err instanceof TryOnError ? err.message : `No se pudo generar: ${(err as Error).message}`;
    track("generate_result", { ok: false, style: styleId ?? "", code: err instanceof TryOnError ? err.code : "EXCEPTION", shot: shotId });
    setStatus(message, true);
    updateButton();
  }
}

/** Comprobaciones automáticas en segundo plano y registro en Firestore (sin imágenes). */
async function logQuality(shot: Shot, result: Blob, ms: number, composed: boolean): Promise<void> {
  try {
    const selfieBlob = selfieDataUrl ? await (await fetch(selfieDataUrl)).blob() : null;
    const [yawSelfie, yawResult] = await Promise.all([
      selfieBlob ? faceYaw(selfieBlob).catch(() => null) : Promise.resolve(null),
      faceYaw(result).catch(() => null),
    ]);
    const checks = summarizeChecks(yawSelfie, yawResult);
    track("quality_checks", { style: shot.styleId, faceDetected: checks.faceDetected, yawDrift: checks.yawDrift, composed });
    if (!FUNCTIONS_URL) return;
    const { id } = await logGeneration(FUNCTIONS_URL, {
      shotId: shot.shotId,
      styleId: shot.styleId,
      hairstyleId: shot.hairstyleId,
      beautify,
      padded: selfiePadded,
      composed,
      aspect: selfieAspect,
      ms,
      checks,
    });
    shot.generationId = id;
  } catch (err) {
    console.warn("Registro de calidad no enviado:", (err as Error).message);
  }
}

function attachRating(shot: Shot): void {
  const rate = shot.card.querySelector<HTMLElement>(".rate")!;
  const reasons = shot.card.querySelector<HTMLElement>(".reasons")!;
  const send = (rating: 1 | -1, reason?: string) => {
    track("feedback", { style: shot.styleId, hairstyle: shot.hairstyleId, rating, reason });
    if (shot.generationId && FUNCTIONS_URL) void rateGeneration(FUNCTIONS_URL, shot.generationId, rating, reason).catch(() => undefined);
  };
  rate.querySelectorAll<HTMLButtonElement>(".rate-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      const rating = Number(btn.dataset.rating) as 1 | -1;
      rate.querySelectorAll(".rate-btn").forEach((b) => b.classList.toggle("active", b === btn));
      if (rating === 1) {
        send(1);
        rate.querySelector("span")!.textContent = "¡Gracias!";
        reasons.hidden = true;
      } else {
        reasons.hidden = false;
      }
    });
  });
  reasons.querySelectorAll<HTMLButtonElement>(".chipbtn").forEach((btn) => {
    btn.addEventListener("click", () => {
      send(-1, btn.dataset.reason);
      reasons.hidden = true;
      rate.querySelector("span")!.textContent = "Gracias, nos sirve para mejorar";
    });
  });
}

const VIEW_NAMES: Record<string, string> = { right: "de lado", left: "de lado", back: "de espaldas" };

/**
 * Las dos vistas de lado deben mirar hacia lados distintos. El modelo a veces repite el
 * mismo lado; si el detector de cara ve el mismo giro en las dos, se espeja la segunda.
 */
async function ensureOppositeSides(right: Blob, left: Blob): Promise<[Blob, Blob]> {
  try {
    const [yr, yl] = await Promise.all([faceYaw(right), faceYaw(left)]);
    if (yr !== null && yl !== null && Math.sign(yr) === Math.sign(yl) && Math.abs(yr) > 8 && Math.abs(yl) > 8) {
      console.info(`Vistas de lado hacia el mismo lado (yaw ${yr.toFixed(0)} / ${yl.toFixed(0)}): se espeja la segunda`);
      return [right, await mirrorBlob(left)];
    }
  } catch (err) {
    console.warn("No se pudo comprobar la orientación de las vistas:", (err as Error).message);
  }
  return [right, left];
}

async function onViews(shot: Shot, btn: HTMLButtonElement): Promise<void> {
  if (!FUNCTIONS_URL) return;
  btn.disabled = true;
  btn.classList.add("busy");
  btn.querySelector(".label")!.textContent = "Girando la foto… unos 15 segundos";
  try {
    const base64 = await blobToBase64(shot.views[0].clean);
    const { views, remainingToday } = await generateViews(FUNCTIONS_URL, base64, selfieAspect);
    const raw: Partial<Record<"right" | "left" | "back", Blob>> = {};
    for (const v of views) {
      const bytes = Uint8Array.from(atob(v.imageBase64), (c) => c.charCodeAt(0));
      raw[v.view] = new Blob([bytes], { type: v.mimeType });
    }
    if (raw.right && raw.left) [raw.right, raw.left] = await ensureOppositeSides(raw.right, raw.left);
    for (const key of ["right", "left", "back"] as const) {
      const blob = raw[key];
      if (!blob) continue;
      const marked = await watermarkOnly(blob, shot.label, qrUrlFor(shot));
      appendView(shot, { blob: marked, clean: blob, url: URL.createObjectURL(marked), caption: `${shot.name} · ${VIEW_NAMES[key]}` });
    }
    btn.remove();
    showView(shot, 1);
    setStatus(remainingToday > 0 ? `Te quedan ${remainingToday} fotos hoy` : "Esta era tu última foto de hoy");
  } catch (err) {
    btn.disabled = false;
    btn.classList.remove("busy");
    btn.querySelector(".label")!.textContent = "Ver de lado y de espaldas";
    setStatus(err instanceof TryOnError ? err.message : `No se pudieron generar las vistas: ${(err as Error).message}`, true);
  }
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let s = "";
  for (let i = 0; i < buf.length; i += 0x8000) s += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(s);
}

async function onShare(): Promise<void> {
  const shot = shots[currentShot];
  if (!shot) return;
  track("share", { style: shot.styleId, hairstyle: shot.hairstyleId, shot: shot.shotId, kind: !shot.card.querySelector<HTMLVideoElement>(".orbit")!.hidden ? "video" : "photo", view: shot.currentView });
  // Lo que viaja con la foto lleva su propia etiqueta: QR de canal «compartido» y enlace al probador.
  const text = shareText(shot.name, tryOnUrl(location.origin, shot));
  const videoEl = shot.card.querySelector<HTMLVideoElement>(".orbit")!;
  if (!videoEl.hidden && shot.orbitUrl) {
    const vblob = await (await fetch(shot.orbitUrl)).blob();
    const vfile = new File([vblob], `dreadlove-${shot.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-360.mp4`, { type: "video/mp4" });
    if (navigator.canShare?.({ files: [vfile] })) {
      try {
        await navigator.share({ files: [vfile], title: "Mi look Dreadlove en 360°", text });
      } catch (err) {
        if ((err as DOMException).name !== "AbortError") setStatus("No se pudo compartir", true);
      }
      return;
    }
  }
  const view = currentViewOf(shot);
  const shared = await watermarkOnly(view.clean, shot.label, buyUrl(shot.productUrl, "compartido", shot)).catch(() => view.blob);
  const file = new File([shared], `dreadlove-${view.caption.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.jpg`, { type: shared.type });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "Mi look Dreadlove", text });
    } catch (err) {
      if ((err as DOMException).name !== "AbortError") setStatus("No se pudo compartir", true);
    }
    return;
  }
  const a = document.createElement("a");
  const downloadUrl = URL.createObjectURL(shared);
  a.href = downloadUrl;
  a.download = file.name;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 60_000);
}

/** Las fotos ya generadas se quedan en la galería; solo volvemos al selector de color. */
function onAgain(): void {
  track("try_another");
  setStatus("");
  el<HTMLElement>("step-style").scrollIntoView({ behavior: "smooth", block: "start" });
}

// ?debug: gancho para probar la composición sin pasar por la función.
if (new URLSearchParams(location.search).has("debug")) {
  (window as unknown as { __compose: unknown }).__compose = async (originalUrl: string, generatedUrl: string) => {
    if (!segmenter) throw new Error("segmentador no listo");
    const generated = await (await fetch(generatedUrl)).blob();
    const blob = await composeHairOnly(originalUrl, generated, segmenter, { watermark: true, label: "Cherry Lagoon · dreadlovearg.com", qrUrl: "https://www.dreadlovearg.com/productos/cabeza-completa-cherry-lagoon-o1grf/" });
    addShot("debug", "#", { blob: blob.blob, clean: blob.clean, url: URL.createObjectURL(blob.blob), caption: "debug" }, { styleId: "auburn", hairstyleId: "suelto", selfieBase64: "", shotId: newShotId(), label: "debug" });
    return blob.blob.size;
  };
}

renderStyles();
renderHairstyles();
// Llegada a cada paso (una vez por carga): la diferencia con los eventos de acción es donde se ve la fuga.
const stepObserver = new IntersectionObserver(
  (entries) => entries.forEach((e) => e.isIntersecting && trackOnce("step_view", { step: e.target.id.replace(/^step-/, "") })),
  { threshold: 0 },
);
document.querySelectorAll<HTMLElement>("section.step").forEach((s) => stepObserver.observe(s));
// En Android, sin el atributo capture el selector nativo suele abrir directo la galería/archivos
// en vez de ofrecer la cámara (a diferencia de iOS, que siempre muestra las dos opciones). Se fuerza
// la cámara frontal ahí y se deja un link aparte para elegir una foto ya tomada.
const isAndroid = /Android/i.test(navigator.userAgent);
if (isAndroid) {
  el<HTMLInputElement>("selfie-input").capture = "user";
  el<HTMLButtonElement>("from-gallery").hidden = false;
}
// Tocó «Sacar o subir mi selfie»: junto con selfie_loaded mide a quien abre la cámara y no vuelve.
el<HTMLInputElement>("selfie-input").addEventListener("click", () => track("selfie_open", { has_selfie: hasSelfie(), source: "camera" }));
el<HTMLInputElement>("gallery-input").addEventListener("click", () => track("selfie_open", { has_selfie: hasSelfie(), source: "gallery" }));
for (const id of ["selfie-input", "gallery-input"]) {
  el<HTMLInputElement>(id).addEventListener("change", (e) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) void onSelfie(file);
    input.value = "";
  });
}
el<HTMLButtonElement>("from-gallery").addEventListener("click", () => el<HTMLInputElement>("gallery-input").click());
document.querySelectorAll<HTMLButtonElement>(".level").forEach((btn) => {
  btn.addEventListener("click", () => {
    beautify = Number(btn.dataset.level);
    document.querySelectorAll<HTMLButtonElement>(".level").forEach((b) => {
      const active = b === btn;
      b.classList.toggle("active", active);
      b.setAttribute("aria-pressed", String(active));
    });
    el<HTMLDivElement>("beauty-card").classList.toggle("on", beautify > 0);
    el<HTMLElement>("beauty-hint").textContent = BEAUTY_HINTS[beautify];
    track("set_beautify", { level: beautify });
  });
});
el<HTMLButtonElement>("mirror").addEventListener("click", () => {
  mirror = !mirror;
  if (selfieFile) void onSelfie(selfieFile);
});
el<HTMLButtonElement>("generate").addEventListener("click", () => {
  if (hasSelfie()) {
    void onGenerate();
    return;
  }
  el<HTMLElement>("step-selfie").scrollIntoView({ behavior: "smooth", block: "center" });
  el<HTMLInputElement>("selfie-input").click();
});
el<HTMLButtonElement>("share").addEventListener("click", () => void onShare());
el<HTMLAnchorElement>("buy").addEventListener("click", () => {
  const shot = shots[currentShot];
  track("buy_click", { style: shot?.styleId ?? "", hairstyle: shot?.hairstyleId ?? "", shot: shot?.shotId ?? "" });
});
el<HTMLButtonElement>("again").addEventListener("click", onAgain);
